/*
 * The unified shell's navigation drawer below `md`. The scrim and the timings are the design
 * system's `Modal` (`common/generic/components/modal/Modal.module.css`) — one overlay treatment in
 * the product, whatever surface is over it — and what differs is the panel's geometry: it holds the
 * inline-start edge and runs the full block size, where a modal is a centred box.
 */

.overlay {
  position: fixed;
  inset-block-start: 0;
  inset-inline-start: 0;
  inline-size: 100dvw;
  block-size: 100dvh;
  /* The same step `Modal` takes, and for the same reason: above the global header (1000) and any
     sticky page chrome, which set an explicit stacking order this would otherwise lose to. */
  z-index: 1100;
  background-color: oklch(from var(--color__black) l c h / 0.6);
}

.overlay[data-entering] {
  animation: drawer-fade 250ms;
}

.overlay[data-exiting] {
  animation: drawer-fade 250ms reverse;
}

/*
 * The panel. `20rem` is the pane's own `md` width (256px) plus the chin's inset, capped at 85% so a
 * narrow phone keeps a strip of scrim to tap back out through — a panel that reaches the far edge
 * reads as a page, and then the scrim is not discoverable as a way to close it.
 */
.panel {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  inline-size: 20rem;
  max-inline-size: 85%;
  block-size: 100%;
  border-inline-end: 1px solid var(--color__gray-300);
  background: var(--color__white);
  overscroll-behavior: contain;
}

.panel[data-entering] {
  animation: drawer-slide-in 250ms;
}

.panel[data-exiting] {
  animation: drawer-slide-out 250ms;
}

/*
 * `min-block-size: 0` so the rail below the chin is what scrolls, rather than the dialog growing
 * past the panel and taking the close button off screen with it.
 */
.dialog {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  outline: none;
}

/*
 * The close control sits on its own row at the head of the panel, at the top bar's height, so the
 * rail below it starts where the page's own content starts and the two edges agree.
 */
.chin {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  block-size: 50px;
  padding-inline: var(--space__sm);
}

.close {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--space__3xl);
  block-size: var(--space__3xl);
  padding: 0;
  border: none;
  border-radius: var(--border-radius__lg);
  background: transparent;
  color: var(--color__gray-900);
  cursor: pointer;
}

.close[data-hovered] {
  background-color: var(--color__gray-100);
}

.close[data-focus-visible] {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
}

/*
 * The search sits between the chin and the rail, `sm` in from the panel edge so its box lines up
 * with the nav rows' own inset rather than with the panel's border.
 */
.search {
  flex: 0 0 auto;
  padding: 0 var(--space__sm) var(--space__md);
}

.rail {
  flex: 1 1 auto;
  min-block-size: 0;
  overflow-y: auto;
}

@keyframes drawer-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes drawer-slide-in {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
}

@keyframes drawer-slide-out {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-100%);
  }
}
